import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from '../theme';

/**
 * The stepper for the create-and-edit flows, drawn to the module-screens book's
 * "Stepped page" spec.
 *
 * Two changes of meaning from what this was:
 *
 * A finished step is *green*, not plum. The book uses the status success colour
 * for completed steps, which frees plum to mean only "you are here" — before,
 * done and current were both plum and differed by fill alone.
 *
 * Steps are named, not numbered. The book: "Four steps at most, named for what
 * they contain rather than numbered." A number tells you how far through you
 * are; the name tells you what the step wants, which is the question people
 * actually have. The circle carries a check or nothing at all.
 *
 * Props unchanged, so the sixteen existing call sites keep working:
 *   active : string[]  — labels of completed-or-current steps
 *   page   : string    — which flow's step list to use
 */
const STEP_SETS = {
  // NOTE: five steps, where the book allows four. Merging "Approval chain" into
  // "Assign Policy" is a product decision rather than a styling one, so it is
  // flagged here rather than done quietly.
  policy: ["Policy Details", "Add Category", "Assign Policy", "Approval chain", "Review"],
  shift: ["Shift Details", "Assign Shift"],
  "shift-rotation": ["Shift", "Frequency", "Assign"],
  "pay-group": ["Pay Group"],
  "pay-group-details": ["Pay Group Details"],
};

// Plum at the weight the book rings the current step with. Lighter than the
// solid and darker than the tint, so it reads as a halo rather than a border.
const CURRENT_RING = "#E8CBD6";

const Connector = ({ filled, hidden }) => (
  <Box
    aria-hidden="true"
    sx={{
      flex: 1,
      height: hidden ? 0 : "2px",
      background: hidden ? "transparent" : filled ? tokens.success : tokens.border,
    }}
  />
);

const ActiveBar = ({ active = [], page }) => {
  const items = STEP_SETS[page] || STEP_SETS.policy;

  // current step = last label present in `active`; everything before it is done
  const currentIndex = useMemo(() => {
    let idx = 0;
    items.forEach((label, i) => { if (active.includes(label)) idx = i; });
    return idx;
  }, [items, active]);

  return (
    <Box
      component="ol"
      aria-label="Progress"
      sx={{
        flex: 1,
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        listStyle: "none",
        margin: 0,
        padding: 0,
        mr: "1rem",
      }}
    >
      {items.map((label, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        return (
          <Box component="li" key={label} aria-current={current ? "step" : undefined}>
            <Box sx={{ display: "flex", alignItems: "center", height: "20px", mb: "10px" }}>
              <Connector filled={done || current} hidden={i === 0} />
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  flex: "none",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done ? tokens.success : current ? tokens.primary : "transparent",
                  border: current
                    ? `3px solid ${CURRENT_RING}`
                    : done
                      ? "none"
                      : `2px solid ${tokens.borderStrong}`,
                  color: tokens.onPrimary,
                }}
              >
                {done && <CheckIcon sx={{ fontSize: "0.75rem" }} />}
              </Box>
              <Connector filled={done} hidden={i === items.length - 1} />
            </Box>
            <Box
              sx={{
                fontSize: "0.8125rem",
                textAlign: "center",
                // A step still ahead is stated but not competing: the book drops
                // it to the inactive grey rather than keeping it in plum.
                fontWeight: done || current ? 600 : 400,
                color: done || current ? tokens.text : tokens.textInactive,
              }}
            >
              {label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ActiveBar;
